import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { useIntl } from "react-intl";
import { useToasts } from "@magento/peregrine";
import { useCheckoutContext } from "@magento/peregrine/lib/context/checkout";
import { useHistory, useLocation } from "@magento/venia-ui/lib/drivers";
import { useCartContext } from "@overrides_venia/peregrine/context/cart";
import { useCatalogContext } from "@magento/peregrine/lib/context/catalog";
import Modal from "@Components/Modal";
import Icon from "@Components/Icon";
import { ADD_PRODUCT } from "@overrides_venia/venia-ui/RootComponents/Product/product.gql";
import { cdnImageUrl } from "@Src_main/utils/generalUtils";
import { useLocale } from "@Src_main/localeProvider";
import { useUserContext } from "@overrides_venia/peregrine/context/user";
import { env } from "build/env";
import UIkit from "uikit";
import { pickFirstMatchingCampaign } from "./promoModalRules";
import { promoModalCampaigns } from "./promoModalConfig";
import defaultClasses from "./promoModalHost.module.scss";
import { mergeClasses } from "@magento/venia-ui/lib/classify";
const storageKey = (campaignId, suffix) => "miscusi_promo_modal_".concat(campaignId, "_").concat(suffix);
const openPromoOffcanvas = offcanvasId => {
  const elementId = (offcanvasId || "#offcanvas-login-dx").replace(/^#/, "");
  const element = document.getElementById(elementId);
  if (!element) {
    return null;
  }
  try {
    // Keep login panel above the promo modal (UIkit modal default z-index is higher).
    element.style.zIndex = "1020";
    UIkit.offcanvas(element).show();
    return element;
  } catch (error) {
    console.error(error);
    return null;
  }
};
const getOptimizedBackgroundUrl = background => {
  if (!background || background.type !== "image") {
    return "";
  }
  const src = cdnImageUrl(background.src);
  if (background.optimize === false) {
    return src;
  }
  const width = background.width || 560;
  const height = background.height || 800;
  const quality = background.quality || 82;
  const fit = background.fit || "cover";
  const options = "width=".concat(width, ",height=").concat(height, ",fit=").concat(fit, ",quality=").concat(quality, ",format=auto");
  try {
    const url = new URL(src);
    return "".concat(url.origin, "/cdn-cgi/image/").concat(options).concat(url.pathname).concat(url.search);
  } catch (_unused) {
    return src;
  }
};
const PromoModalHost = props => {
  const {
    classes: propClasses
  } = props;
  const classes = mergeClasses(defaultClasses, propClasses);
  const {
    formatMessage
  } = useIntl();
  const {
    language
  } = useLocale();
  const location = useLocation();
  const history = useHistory();
  const [, {
    addToast
  }] = useToasts();
  const [{
    isSignedIn
  }] = useUserContext();
  const [{
    tableData
  }] = useCheckoutContext();
  const [{
    cartId
  }, {
    updateCartData,
    setIsLoadingCart
  }] = useCartContext();
  const [{
    filtered_sku: filteredSku,
    onlyItemAtPriceWhitelist
  }] = useCatalogContext();
  const [addProductToCart] = useMutation(ADD_PRODUCT);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [ctaBusy, setCtaBusy] = useState(false);
  const storeLsId = tableData && tableData.store && tableData.store.ls_store_id;
  const storeCode = tableData && (tableData.store && tableData.store.cps || tableData.cps);
  const storeViewOrder = tableData && tableData.store && tableData.store.storeViewOrder;
  const siteStoreViewCode = env.store_view_code;
  const matchContext = useMemo(() => ({
    now: new Date(),
    pathname: location.pathname,
    language,
    availableSkus: filteredSku,
    storeCode,
    storeLsId,
    storeViewOrder,
    siteStoreViewCode,
    isSignedIn
  }), [filteredSku, isSignedIn, language, location.pathname, siteStoreViewCode, storeCode, storeLsId, storeViewOrder]);
  const isDismissed = useCallback(campaign => {
    if (!campaign || campaign.dismissPersistence === "none") {
      return false;
    }
    const key = campaign.dismissPersistence === "forever" ? storageKey(campaign.id, "closed") : storageKey(campaign.id, "session_closed");
    try {
      if (campaign.dismissPersistence === "forever") {
        return window.localStorage.getItem(key) === "1";
      }
      return window.sessionStorage.getItem(key) === "1";
    } catch (_unused2) {
      return false;
    }
  }, []);
  const markDismissed = useCallback(campaign => {
    if (!campaign || campaign.dismissPersistence === "none") {
      return;
    }
    const key = campaign.dismissPersistence === "forever" ? storageKey(campaign.id, "closed") : storageKey(campaign.id, "session_closed");
    try {
      if (campaign.dismissPersistence === "forever") {
        window.localStorage.setItem(key, "1");
      } else {
        window.sessionStorage.setItem(key, "1");
      }
    } catch (_unused3) {
      // ignore quota / private mode
    }
  }, []);
  useEffect(() => {
    setIsOpen(false);
    setActiveCampaign(null);
    if (onlyItemAtPriceWhitelist !== null && onlyItemAtPriceWhitelist !== void 0 && onlyItemAtPriceWhitelist.length) {
      return undefined;
    }
    const chosen = pickFirstMatchingCampaign(promoModalCampaigns, matchContext);
    if (!chosen || isDismissed(chosen)) {
      return undefined;
    }
    const delay = typeof chosen.openDelayMs === "number" ? chosen.openDelayMs : 0;
    const t = window.setTimeout(() => {
      setActiveCampaign(chosen);
      setIsOpen(true);
    }, delay);
    return () => {
      window.clearTimeout(t);
    };
  }, [matchContext, isDismissed, onlyItemAtPriceWhitelist]);
  const handleClose = useCallback(() => {
    if (activeCampaign) {
      markDismissed(activeCampaign);
    }
    setIsOpen(false);
  }, [activeCampaign, markDismissed]);
  const handlePrimary = useCallback(async () => {
    if (!activeCampaign) {
      return;
    }
    const cta = activeCampaign.primaryCta;
    if (cta.action === "dismiss") {
      markDismissed(activeCampaign);
      setIsOpen(false);
      return;
    }
    if (cta.action === "navigate" && cta.href) {
      markDismissed(activeCampaign);
      setIsOpen(false);
      history.push(cta.href);
      return;
    }
    if (cta.action === "open_offcanvas") {
      const offcanvasId = cta.offcanvasId || "#offcanvas-login-dx";
      markDismissed(activeCampaign);
      const offcanvasEl = openPromoOffcanvas(offcanvasId);
      if (!offcanvasEl) {
        setIsOpen(false);
        history.push(formatMessage({
          id: "url.key.login"
        }));
        return;
      }

      // Let the offcanvas slide over the promo, then close the promo underneath.
      let promoClosed = false;
      const silentlyClosePromo = () => {
        if (promoClosed) {
          return;
        }
        promoClosed = true;
        UIkit.util.off(offcanvasEl, "shown", silentlyClosePromo);
        setIsOpen(false);

        // If closing the modal cancelled the panel, bring it back once.
        window.setTimeout(() => {
          if (!offcanvasEl.classList.contains("uk-open")) {
            openPromoOffcanvas(offcanvasId);
          }
        }, 50);
      };
      UIkit.util.on(offcanvasEl, "shown", silentlyClosePromo);
      // Fallback if `shown` does not fire for any reason.
      window.setTimeout(silentlyClosePromo, 400);
      return;
    }
    if (cta.action === "add_to_cart") {
      if (!cta.sku) {
        return;
      }
      if (!cartId) {
        addToast({
          type: "error",
          message: formatMessage({
            id: "promoModal.errors.missingCart"
          }),
          timeout: 4000,
          icon: /*#__PURE__*/React.createElement(Icon, {
            name: "icon-error"
          })
        });
        return;
      }
      setCtaBusy(true);
      setIsLoadingCart();
      try {
        const {
          data,
          errors
        } = await addProductToCart({
          fetchPolicy: "no-cache",
          variables: {
            cartId,
            cartItems: [{
              sku: cta.sku,
              quantity: cta.quantity || 1,
              selected_options: []
            }]
          }
        });
        if (errors && errors.length) {
          throw new Error(errors.map(e => e.message).join("; "));
        }
        if (data && data.addProductsToCart && data.addProductsToCart.cart) {
          updateCartData(data.addProductsToCart.cart);
        }
        markDismissed(activeCampaign);
        setIsOpen(false);
      } catch (e) {
        addToast({
          type: "error",
          message: formatMessage({
            id: "promoModal.errors.addToCart"
          }),
          timeout: 5000,
          icon: /*#__PURE__*/React.createElement(Icon, {
            name: "icon-error"
          })
        });
      } finally {
        setCtaBusy(false);
      }
    }
  }, [activeCampaign, addProductToCart, addToast, cartId, formatMessage, history, markDismissed, setIsLoadingCart, updateCartData]);
  if (!activeCampaign) {
    return null;
  }
  const {
    template,
    background,
    titleMessageId,
    bodyMessageId,
    primaryCta,
    dismissMessageId,
    showDismiss = true,
    ctaOnly = false
  } = activeCampaign;
  const dismissId = dismissMessageId || "promoModal.demo.dismiss";
  const bgStyle = background.type === "color" ? {
    backgroundColor: background.value
  } : undefined;
  const backgroundPosition = background.type === "image" ? background.position || "center" : undefined;
  const rootClass = template === "compact" ? classes.rootCompact : classes.rootHero;
  const contentCardStyle = activeCampaign.contentBackgroundColor ? {
    background: activeCampaign.contentBackgroundColor
  } : null;
  const showTitle = Boolean(titleMessageId);
  const showBody = Boolean(bodyMessageId);
  const contentCardClass = ctaOnly ? null : showTitle || showBody ? classes.contentCard : "".concat(classes.contentCard, " ").concat(classes.contentCardActionsOnly);
  const scrimClass = ctaOnly ? "".concat(classes.scrim, " ").concat(classes.scrimCtaOnly) : classes.scrim;
  const primaryBtnClass = ctaOnly ? "".concat(classes.primaryBtn, " ").concat(classes.primaryBtnProminent) : classes.primaryBtn;
  const heroAspectRatio = template === "hero" && background.type === "image" && background.intrinsicWidth && background.intrinsicHeight ? "".concat(background.intrinsicWidth, " / ").concat(background.intrinsicHeight) : null;
  const modalDialogStyle = heroAspectRatio ? {
    "--promo-modal-aspect-ratio": heroAspectRatio
  } : undefined;
  return /*#__PURE__*/React.createElement(Modal, {
    id: "promo-modal-".concat(activeCampaign.id),
    isOpen: isOpen,
    onClose: handleClose,
    modalDialogClass: "uk-border-rounded uk-overflow-hidden ".concat(classes.dialog, " ").concat(template === "hero" ? classes.dialogHero : ""),
    modalDialogStyle: modalDialogStyle,
    classNameExtra: classes.modalRoot,
    customZIndex: 990,
    closeButton: false,
    centeredModal: template !== "hero"
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.inner, " ").concat(rootClass),
    style: bgStyle
  }, background.type === "image" ? /*#__PURE__*/React.createElement("img", {
    className: classes.backgroundImage,
    src: getOptimizedBackgroundUrl(background),
    alt: background.altMessageId ? formatMessage({
      id: background.altMessageId
    }) : "",
    style: backgroundPosition ? {
      objectPosition: backgroundPosition
    } : undefined
  }) : null, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: classes.closeButton,
    onClick: handleClose,
    "aria-label": formatMessage({
      id: dismissId
    }),
    disabled: ctaBusy
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "icon-close",
    color: "white",
    header: true
  })), /*#__PURE__*/React.createElement("div", {
    className: scrimClass
  }, ctaOnly ? /*#__PURE__*/React.createElement("div", {
    className: classes.ctaDock
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: primaryBtnClass,
    style: {
      backgroundColor: primaryCta.backgroundColor || "#fff",
      color: primaryCta.textColor || "#222"
    },
    disabled: ctaBusy,
    "aria-busy": ctaBusy,
    onClick: handlePrimary
  }, ctaBusy ? /*#__PURE__*/React.createElement("span", {
    className: classes.buttonSpinner,
    "uk-spinner": "ratio: 0.65",
    "aria-hidden": false
  }) : null, /*#__PURE__*/React.createElement("span", {
    className: classes.primaryBtnLabel
  }, formatMessage({
    id: primaryCta.labelMessageId
  }))), showDismiss ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: classes.secondaryBtn,
    onClick: handleClose,
    disabled: ctaBusy
  }, formatMessage({
    id: dismissId
  })) : null)) : /*#__PURE__*/React.createElement("div", {
    className: contentCardClass,
    style: contentCardStyle
  }, showTitle ? /*#__PURE__*/React.createElement("h2", {
    className: classes.title
  }, formatMessage({
    id: titleMessageId
  })) : null, showBody ? /*#__PURE__*/React.createElement("p", {
    className: classes.body
  }, formatMessage({
    id: bodyMessageId
  })) : null, /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: primaryBtnClass,
    style: {
      backgroundColor: primaryCta.backgroundColor || "#fff",
      color: primaryCta.textColor || "#222"
    },
    disabled: ctaBusy,
    "aria-busy": ctaBusy,
    onClick: handlePrimary
  }, ctaBusy ? /*#__PURE__*/React.createElement("span", {
    className: classes.buttonSpinner,
    "uk-spinner": "ratio: 0.65",
    "aria-hidden": false
  }) : null, /*#__PURE__*/React.createElement("span", {
    className: classes.primaryBtnLabel
  }, formatMessage({
    id: primaryCta.labelMessageId
  }))), showDismiss ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: classes.secondaryBtn,
    onClick: handleClose,
    disabled: ctaBusy
  }, formatMessage({
    id: dismissId
  })) : null)))));
};
export default PromoModalHost;